/**
 * Operation descriptor: a serializable record of a single applied tool action.
 *
 * The operation log stores an ordered list of these per checkpoint step. Replaying
 * the base scan mesh through every descriptor up to a target step reconstructs the
 * model exactly as it was *at that step* — instead of loading a single full mesh
 * that may already contain later work.
 *
 * Descriptors must be JSON-serializable so they can be persisted in the server
 * operations manifest (see `bkCheckpointUpload`). Heavy geometry never lives here;
 * geometry is reproduced by the registered replay handler from `params`, or, for
 * not-yet-migrated tools, recovered from the per-step mesh snapshot fallback.
 *
 * @typedef {Object} OperationDescriptor
 * @property {string} toolId   Stable tool identifier, matched against the operation registry.
 * @property {string} stepId   Workflow step that produced the operation (GLOBAL_STEP_ORDER id).
 * @property {Record<string, unknown>} params  Serializable parameters needed to replay.
 * @property {number} at       Capture timestamp (ms epoch).
 * @property {number} seq      Monotonic sequence within the step (apply order).
 * @property {number|null} durationMs  How long the apply's geometry work took.
 * @property {number|null} vertexCount Socket size the apply ran against.
 * @property {number|null} deviceVerticesPerMs  Throughput of the machine that
 *   recorded `durationMs`, so another device can scale it instead of trusting a
 *   millisecond figure from unknown hardware.
 */

/** Bump when the descriptor shape or manifest layout changes. */
export const OPERATION_LOG_SCHEMA_VERSION = 2;

/**
 * @param {string} toolId
 * @param {string} stepId
 * @param {Record<string, unknown>} [params]
 * @param {number} [seq]
 * @param {{ durationMs?: number|null, vertexCount?: number|null, deviceVerticesPerMs?: number|null }} [metrics]
 * @returns {OperationDescriptor}
 */
export function createOperationDescriptor(toolId, stepId, params = {}, seq = 0, metrics = {}) {
  return {
    toolId,
    stepId,
    // Deep-copy so later mutation of the source store object can't corrupt the log.
    params: safeClone(params),
    at: Date.now(),
    seq,
    durationMs: Number.isFinite(metrics.durationMs) ? metrics.durationMs : null,
    vertexCount: Number.isFinite(metrics.vertexCount) ? metrics.vertexCount : null,
    deviceVerticesPerMs: Number.isFinite(metrics.deviceVerticesPerMs)
      ? metrics.deviceVerticesPerMs
      : null,
  };
}

/**
 * JSON round-trip clone; falls back to a shallow copy for exotic values so a
 * non-serializable param never throws at capture time (it just won't replay).
 * @template T
 * @param {T} value
 * @returns {T}
 */
export function safeClone(value) {
  if (value == null || typeof value !== 'object') return value;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return Array.isArray(value) ? [...value] : { ...value };
  }
}
